<script setup lang="ts">
import { ref } from "vue";
import { trackAppFeature } from "@/analytics/app-analytics";
import { useTranslation } from "i18next-vue";
import { useChatbotStore } from "@/stores/chatbotStore";
import ChatbotMessageList from "./ChatbotMessageList.vue";
import ChatbotInput from "./ChatbotInput.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const { t } = useTranslation();
const chatbotStore = useChatbotStore();

const emit = defineEmits<{
  "update:chatOpen": [value: boolean];
}>();

const isChatOpen = ref(false);

function openChat() {
  trackAppFeature("chatbot_opened");
  isChatOpen.value = true;
  emit("update:chatOpen", true);
}

function backToHelp() {
  isChatOpen.value = false;
  emit("update:chatOpen", false);
}

function handleSend(text: string) {
  chatbotStore.sendMessage(text);
}

function clearConversation() {
  chatbotStore.clearConversation();
}
</script>

<template>
  <!-- Banner button to open chat -->
  <div v-if="!isChatOpen" class="chatbot-banner" @click="openChat">
    <div class="chatbot-banner-icon">
      <PhosphorIcon name="robot" :size="20" weight="fill" />
    </div>
    <div class="chatbot-banner-content">
      <div class="chatbot-banner-title">
        {{ t("help.chatbot.bannerTitle") }}
      </div>
      <div class="chatbot-banner-description">
        {{ t("help.chatbot.bannerDescription") }}
      </div>
    </div>
    <PhosphorIcon
      name="caret-right"
      :size="14"
      weight="bold"
      class="chatbot-banner-arrow"
    />
  </div>

  <!-- Chat interface - full page mode -->
  <div v-else class="chatbot-fullpage">
    <div class="chatbot-fullpage-header">
      <div class="d-flex align-items-center gap-2">
        <button class="chatbot-back-btn" @click="backToHelp">
          <PhosphorIcon name="arrow-left" :size="18" weight="bold" />
        </button>
        <div class="chatbot-header-info">
          <div class="chatbot-header-title">
            {{ t("help.chatbot.title") }}
          </div>
          <div class="chatbot-header-status">
            <span class="status-dot"></span>
            {{ t("help.chatbot.online") }}
          </div>
        </div>
      </div>
      <button
        v-if="chatbotStore.messages.length > 0"
        class="chatbot-clear-btn"
        @click="clearConversation"
      >
        <PhosphorIcon name="arrow-counter-clockwise" :size="18" weight="bold" />
      </button>
    </div>

    <div class="chatbot-fullpage-messages">
      <ChatbotMessageList
        :messages="chatbotStore.messages"
        :loading="chatbotStore.isLoading"
      />
    </div>

    <div class="chatbot-fullpage-input">
      <ChatbotInput :loading="chatbotStore.isLoading" @send="handleSend" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Banner button style */
.chatbot-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15);
  }
}

.chatbot-banner-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #2563eb;
  border-radius: 10px;
  color: white;
  font-size: 20px;
  flex-shrink: 0;
}

.chatbot-banner-content {
  flex: 1;
  min-width: 0;
}

.chatbot-banner-title {
  font-weight: 600;
  font-size: 14px;
  color: #1e40af;
  margin-bottom: 2px;
}

.chatbot-banner-description {
  font-size: 12px;
  color: #3b82f6;
}

.chatbot-banner-arrow {
  color: #1e40af;
  font-size: 14px;
  flex-shrink: 0;
}

/* Full page chat mode */
.chatbot-fullpage {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  margin: -16px;
  margin-top: -8px;
  background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}

.chatbot-fullpage-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 10px 20px;
  background: #2563eb;
  color: white;
  flex-shrink: 0;
}

.chatbot-back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
}

.chatbot-header-info {
  display: flex;
  flex-direction: column;
}

.chatbot-header-title {
  font-weight: 600;
  font-size: 15px;
  line-height: 1.2;
}

.chatbot-header-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  opacity: 0.85;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #4ade80;
}

.chatbot-clear-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: white;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
}

.chatbot-fullpage-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 20px;
}

.chatbot-fullpage-input {
  flex-shrink: 0;
  padding: 12px 20px 24px 20px;
  background: white;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}
</style>
